import EthereumService from '@/services/ethereum'
import { findTokenFromAddr } from '@/utils/token'
import bignumber from '@/utils/bignumber'
import cookie from '@/utils/cookie'
import KulapService from '@/services/kulap'
import { PARTNER_INDEX, DEFAULT_PRICE_SLIPPAGE } from '../../constants'

export default class Service extends EthereumService {
  constructor (options) {
    super()
    this.kulapApi = options.kulapApi
    this.kulapDex = options.kulapDex
    this.store = options.store
  }

  async getBestRateFromFromAmount (fromToken, toToken, fromHumanAmount) {
    if (this.store.state.currentWeb3) {
      this.web3 = this.store.state.currentWeb3
    }
    const wallets = await this.getWallets()
    const amount = fromHumanAmount.toWei(fromToken.decimal).toString(10)
    const kulapDex = new KulapService()
    await kulapDex.init()
    const { data } = await this.kulapApi.getBestRateFromFromAmount(fromToken.code, toToken.code, amount)
    const minReturnAmount = bignumber(data.STD.trade.toAmount).times(bignumber(100).minus(DEFAULT_PRICE_SLIPPAGE)).idiv(100)
    let estimateGas = null
    try {
      const option = {
        from: wallets.shift(),
        value: fromToken.code === 'ETH' ? amount : 0
      }
      const gasLimit = await kulapDex.contract.methods.trade(
        data.STD.trade.routes[0],
        fromToken.address,
        kulapDex.web3.utils.toBN(amount),
        toToken.address,
        minReturnAmount,
        PARTNER_INDEX
      ).estimateGas(option)
      estimateGas = kulapDex.adjustGasLimit(gasLimit)
    } catch (error) {
      console.log(error)
    }

    Object.keys(data).forEach((speed) => {
      data[speed].gasPrice = bignumber(data[speed].gasPrice)
      data[speed].gasLimit = bignumber(estimateGas === null ? data[speed].gasLimit : estimateGas)
      data[speed].trade.fromAmount = bignumber(data[speed].trade.fromAmount)
      data[speed].trade.rate = bignumber(data[speed].trade.rate)
    })
    return data
  }

  async getBestRateFromToAmount (fromToken, toToken, toHumanAmount) {
    if (this.store.state.currentWeb3) {
      this.web3 = this.store.state.currentWeb3
    }
    const wallets = await this.getWallets()
    const amount = toHumanAmount.toWei(toToken.decimal).toString(10)
    const kulapDex = new KulapService()
    await kulapDex.init()
    const { data } = await this.kulapApi.getBestRateFromToAmount(fromToken.code, toToken.code, amount)
    const fromAmount = data.STD.trade.fromAmount
    const minReturnAmount = bignumber(data.STD.trade.toAmount).times(bignumber(100).minus(DEFAULT_PRICE_SLIPPAGE)).idiv(100)
    let estimateGas = null
    try {
      const option = {
        from: wallets.shift(),
        value: fromToken.code === 'ETH' ? fromAmount : 0
      }
      const gasLimit = await kulapDex.contract.methods.trade(
        data.STD.trade.routes[0],
        fromToken.address,
        kulapDex.web3.utils.toBN(fromAmount),
        toToken.address,
        minReturnAmount,
        PARTNER_INDEX
      ).estimateGas(option)
      estimateGas = kulapDex.adjustGasLimit(gasLimit)
    } catch (error) {
      console.log(error)
    }
    Object.keys(data).forEach((speed) => {
      data[speed].gasPrice = bignumber(data[speed].gasPrice)
      data[speed].gasLimit = bignumber(estimateGas === null ? data[speed].gasLimit : estimateGas)
      data[speed].trade.fromAmount = bignumber(data[speed].trade.fromAmount)
      data[speed].trade.rate = bignumber(data[speed].trade.rate)
    })
    return data
  }

  cancelGetBestRate () {
    this.kulapApi.cancelBestRateRequest()
  }

  async exchange (userAddr, fromToken, toToken, bestRate, maxPriceSlippage, onTxHashed, onTxConfirmed, onReceiptFailed, dataForUI) {
    const minReturnAmount = bignumber(bestRate.trade.toAmount).times(bignumber(100).minus(maxPriceSlippage)).idiv(100)
    if (fromToken.code === 'ETH') { // Trade Ether -> Token
      await this.kulapDex.tradeEthertToAny(
        userAddr,
        bestRate.trade.routes[0],
        fromToken.address,
        toToken.address,
        bestRate.trade.fromAmount,
        minReturnAmount,
        bestRate.gasPrice.gweiToWei(),
        bestRate.gasLimit,
        onTxHashed,
        onTxConfirmed,
        onReceiptFailed,
        dataForUI
      )
    } else { // Trade Token -> Any
      await this.kulapDex.tradeTokenToAny(
        userAddr,
        bestRate.trade.routes[0],
        fromToken.address,
        toToken.address,
        bestRate.trade.fromAmount,
        minReturnAmount,
        bestRate.gasPrice.gweiToWei(),
        bestRate.gasLimit,
        onTxHashed,
        onTxConfirmed,
        onReceiptFailed,
        dataForUI
      )
    }
  }

  async isAllowanced (tokenAddr, userAddr) {
    const isAllowanced = await this.kulapDex.tokenAllowance(tokenAddr, userAddr)
    return isAllowanced
  }

  async approveToken (fromTokenAddr, userAddr, gasPrice) {
    return new Promise(async (resolve, reject) => {
      await this.kulapDex.approveToken(fromTokenAddr, userAddr, gasPrice, (hash) => {
        // On Hash
        resolve(hash)
      }, null, (error) => {
        // On Error
        reject(error)
      })
    })
  }

  async pushTransactionHistory (status, hash, network, data, receipt, userWallet, currentNetwork) {
    let payload = {}
    if (status === 'success' && receipt.blockNumber && receipt.from) {
      // Get real rate.
      const events = await this.kulapDex.getPastEvents('Trade', { 'trader': receipt.from }, receipt.blockNumber, receipt.blockNumber)
      const transaction = events.find(e => e.transactionHash === hash)
      const fromAsset = findTokenFromAddr(network, transaction.returnValues.srcAsset)
      const toAsset = findTokenFromAddr(network, transaction.returnValues.destAsset)
      payload = {
        transactionHash: transaction.transactionHash,
        fromAmount: bignumber(transaction.returnValues.srcAmount).toBase(fromAsset.decimal).toString(),
        toAmount: bignumber(transaction.returnValues.destAmount).toBase(toAsset.decimal).toString(),
        status
      }
      localStorage.setItem(`KULAP.noti-latest-block-height-${currentNetwork}-${userWallet}`, receipt.blockNumber) // Set last block number scaned.
    } else {
      payload = {
        transactionHash: hash,
        fromAsset: findTokenFromAddr(network, data.from.address),
        toAsset: findTokenFromAddr(network, data.to.address),
        fromAmount: data.from.amount,
        toAmount: data.to.amount,
        timestamp: bignumber(+new Date() / 1000).toFixed(0),
        detail: {},
        status
      }
    }
    this.store.dispatch('pushTransactionHistory', payload)
  }

  async getTokensBalance (tokens, userAddress) {
    let promiseTasks = []
    for (let token in tokens) {
      const task = new Promise(async (resolve) => {
        if (token !== 'ETH') {
          tokens[token].balance = await this.getTokenBalance(userAddress, tokens[token].address, tokens[token].decimal || tokens[token].decimal)
        } else {
          tokens[token].balance = await this.getEthBalance(userAddress)
        }
        resolve()
      })
      promiseTasks.push(task)
    }
    await Promise.all(promiseTasks)
    return tokens
  }

  async updateUserData () {
    if (cookie.get('accessToken') && cookie.get('user')) {
      const { data } = await this.kulapApi.getUserProfile()
      this.store.dispatch('setUser', data)
    } else {
      this.store.dispatch('setUser', {})
    }
  }

  async checkUserDailyVolume (address, currentWalletProvider, fromToken, fromTokenAmount) {
    if (currentWalletProvider === 'wallet-connect') {
      currentWalletProvider = 'walletConnect'
    }
    const { data } = await this.kulapApi.putUserEthAddress(
      {
        ethAddress: address,
        walletConnectionType: currentWalletProvider,
        isCheckDailyVolume: true,
        options: {
          fromToken,
          fromTokenAmount
        }
      }
    )
    return data && data.isOverDailyVolumeLimit
  }

  async sendObjectiveOfTrading (objective, fromToken, fromTokenAmount) {
    const { data } = await this.kulapApi.postTradingObjective(objective, fromToken, fromTokenAmount)
    return data
  }
}



// WEBPACK FOOTER //
// ./src/features/Trading/service.js